import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RegisterUnknownPatientResult, ServicePointQueueView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { casualtySexOptionsQuery, registerUnknownPatient } from '../api/casualty.ts';
import { ApiError } from '../api/client.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';

// Register an unknown patient in one step (docs/plans/phase2-casualty.md §5). Nothing is
// required: the patient gets a temporary label and joins the casualty queue at once. What
// is typed stays in this dialog's state and the request body, never in a URL.

const NOT_KNOWN = 'not-known';

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function RegisterUnknownDialog({
  isOpen,
  onOpenChange,
  points,
  onRegistered,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Casualty service points at this facility. */
  points: readonly ServicePointQueueView[];
  onRegistered: (result: RegisterUnknownPatientResult) => void;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const sexes = useQuery({ ...casualtySexOptionsQuery, enabled: isOpen });
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  const [pointId, setPointId] = useState<string | null>(null);
  const [sexId, setSexId] = useState<string>(NOT_KNOWN);
  const [age, setAge] = useState('');
  const [description, setDescription] = useState('');
  const chosenPoint = pointId ?? (points.length === 1 ? (points[0]?.id ?? null) : null);
  const concepts = (sexes.data ?? []).filter((c) => c.status === 'active');
  const ageYears = age.trim() === '' ? null : Number(age.trim());
  const ageInvalid =
    ageYears !== null && (!Number.isInteger(ageYears) || ageYears < 0 || ageYears > 130);

  const reset = () => {
    setClientRequestId(crypto.randomUUID());
    setPointId(null);
    setSexId(NOT_KNOWN);
    setAge('');
    setDescription('');
  };
  const register = useMutation({
    mutationFn: () => {
      const sex = concepts.find((c) => c.id === sexId);
      return registerUnknownPatient({
        clientRequestId,
        servicePointId: chosenPoint ?? '',
        sex: sex === undefined ? null : { system: sex.system, code: sex.code },
        birth:
          ageYears === null ? null : { kind: 'estimated', age: { unit: 'years', value: ageYears } },
        description: description.trim() === '' ? null : description.trim(),
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['casualty'] });
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
      toasts.show({
        title: 'Unknown patient registered',
        description: `Temporary label ${result.temporaryLabel}`,
      });
      reset();
      onRegistered(result);
    },
  });
  const ready = online && chosenPoint !== null && !ageInvalid && !register.isPending;

  return (
    <Dialog
      title="Register unknown patient"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) reset();
        onOpenChange(open);
      }}
    >
      <form
        aria-label="Register unknown patient"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) register.mutate();
        }}
      >
        <p>
          Nothing here is required. The patient gets a temporary label and joins the casualty queue
          now. Merge the record into theirs once you know who they are.
        </p>
        {points.length > 1 ? (
          <RadioGroup
            label="Casualty queue"
            value={chosenPoint}
            onChange={setPointId}
            options={points.map((p) => ({ id: p.id, label: p.name }))}
          />
        ) : null}
        {sexes.isPending ? <LoadingBlock label="Loading choices" rows={2} /> : null}
        {sexes.isError ? (
          <Notice tone="info">
            The sex list could not be loaded, so sex is recorded as not known. It can be corrected
            later.
          </Notice>
        ) : null}
        {sexes.isSuccess ? (
          <RadioGroup
            label="Sex"
            orientation="horizontal"
            value={sexId}
            onChange={setSexId}
            options={[
              { id: NOT_KNOWN, label: 'Not known' },
              ...concepts
                .filter((c) => c.code !== 'unknown')
                .map((c) => ({ id: c.id, label: c.display })),
            ]}
          />
        ) : null}
        <TextField
          label="Estimated age in years (optional)"
          inputMode="numeric"
          value={age}
          onChange={setAge}
          {...(ageInvalid ? { errorMessage: 'Enter a whole number of years from 0 to 130.' } : {})}
        />
        <TextArea
          label="Description (optional)"
          description="Where found, clothing, marks. Shown on patient screens only, never on displays."
          value={description}
          onChange={setDescription}
          rows={3}
        />
        {!online ? (
          <Notice tone="info">
            No connection to Wodi. Registering needs the connection; nothing is saved on this
            device.
          </Notice>
        ) : null}
        {points.length === 0 ? (
          <Notice tone="info">
            No casualty queue is set up at this facility. An administrator adds one in Settings &gt;
            Service points.
          </Notice>
        ) : null}
        {register.error === null ? null : (
          <Notice tone="error">
            {errorText(register.error, 'The patient could not be registered. Try again.')}
          </Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!ready}
            isPending={register.isPending}
            pendingLabel="Registering…"
          >
            Register patient
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
